import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Section, 
  Card, 
  Button,
  FadeInUp,
  SlideIn,
  StaggerContainer,
  StaggerItem,
} from '../../../components/common';
import { usePageTitle } from '../../../hooks/usePageTitle';
import {
  OUTDOOR_GALLERY,
} from '../../../constants';

// Mapeando OUTDOOR_GALLERY para incluir datas (dados específicos da página)
const GALLERY_PHOTOS = OUTDOOR_GALLERY.map((photo, index) => ({
  ...photo,
  date: ['Fev 2024', 'Out 2024', 'Set 2024', 'Ago 2024', 'Jul 2024', 'Jun 2024'][index] || 'Nov 2024'
}));

// Próximas imersões
const UPCOMING_IMMERSIONS = [
  {
    date: '14 Dez',
    day: 'Sábado',
    location: 'Parque da Jaqueira',
    theme: 'Holiday Conversations',
    spots: 3,
    time: '9h às 11h'
  },
  {
    date: '21 Dez',
    day: 'Sábado',
    location: 'Café Mergulhão',
    theme: 'Christmas Special',
    spots: 5,
    time: '10h às 12h'
  },
  {
    date: '11 Jan',
    day: 'Sábado',
    location: 'Marco Zero',
    theme: 'New Year Goals',
    spots: 8,
    time: '8h às 10h'
  },
];

// Locais em Recife (alinhado com OUTDOOR_GALLERY)
const RECIFE_LOCATIONS = [
  { 
    icon: 'ri-tree-line', 
    title: 'Parque da Jaqueira', 
    neighborhood: 'Jaqueira, Recife',
    desc: 'Nosso point favorito para aulas matinais com caminhadas e conversação natural em meio à natureza',
    image: OUTDOOR_GALLERY[2]?.url, // Parque da Jaqueira
    topics: ['Nature vocabulary', 'Health & fitness', 'Outdoor activities']
  },
  { 
    icon: 'ri-leaf-line', 
    title: 'Jardim do Baobá', 
    neighborhood: 'Graças, Recife',
    desc: 'Um oásis urbano perfeito para conversas tranquilas e prática de vocabulário sobre natureza e bem-estar',
    image: OUTDOOR_GALLERY[1]?.url, // Jardim do Baobá
    topics: ['Mindfulness vocabulary', 'Environment topics', 'Relaxed conversations']
  },
  { 
    icon: 'ri-cup-line', 
    title: 'Cafeterias', 
    neighborhood: 'Vários bairros',
    desc: 'Furdunço Café, Nosso Café e outras cafeterias especiais para praticar pedidos e small talk',
    image: OUTDOOR_GALLERY[3]?.url, // Furdunço Café
    topics: ['Ordering food & drinks', 'Social English', 'Business networking']
  },
  { 
    icon: 'ri-store-2-line', 
    title: 'Mercado da Torre', 
    neighborhood: 'Torre, Recife',
    desc: 'Ambiente descontraído para praticar vocabulário de compras, comida e negociação',
    image: OUTDOOR_GALLERY[4]?.url, // Mercado da Torre
    topics: ['Shopping vocabulary', 'Food & ingredients', 'Bargaining phrases']
  },
  { 
    icon: 'ri-sun-line', 
    title: 'Praia de Casa Caiada', 
    neighborhood: 'Casa Caiada, Olinda',
    desc: 'Aulas à beira-mar com vista para o oceano, perfeitas para conversas sobre viagens e lazer',
    image: OUTDOOR_GALLERY[0]?.url, // Praia da Casa Caiada
    topics: ['Beach vocabulary', 'Travel conversations', 'Weather & seasons']
  },
];

export default function OutdoorLessons() {
  usePageTitle('Imersão ao Ar Livre');
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [activeLocation, setActiveLocation] = useState(0);
  const [hoveredSpot, setHoveredSpot] = useState<number | null>(null);

  const currentPhoto = GALLERY_PHOTOS.find(p => p.id === selectedPhoto);

  return (
    <>
      {/* Hero com Galeria Interativa */}
      <section className="relative min-h-screen bg-earth-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="container-custom relative z-10 pt-24 sm:pt-28 pb-12 sm:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[70vh] lg:min-h-[80vh]">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-olive-600/90 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
                <i className="ri-map-pin-line"></i>
                Recife, PE
              </span>
              
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Inglês onde a<br />
                <span className="text-olive-400">vida acontece</span>
              </h1>
              
              <p className="text-base sm:text-lg text-sand-200 mb-4 sm:mb-8 leading-relaxed">
                Nossas aulas ao ar livre acontecem nos parques, cafeterias, praias da região metropolitana Recife. Aprenda inglês praticando situações reais em ambientes autênticos.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  to="/contato" 
                  size="lg" 
                  icon="ri-calendar-event-line"
                >
                  Quero conhecer mais
                </Button>
              </div>
            </motion.div>

            {/* Right - Interactive Photo Grid */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="grid grid-cols-3 gap-3">
                {GALLERY_PHOTOS.map((photo, index) => (
                  <motion.button
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo.id)}
                    className={`relative overflow-hidden rounded-xl aspect-square group ${
                      index === 0 ? 'col-span-2 row-span-2' : ''
                    }`}
                    whileHover={{ scale: 1.02, zIndex: 10 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <img 
                      src={photo.url} 
                      alt={photo.location}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-earth-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-sm font-medium truncate">{photo.location}</p>
                      <p className="text-sand-300 text-xs">{photo.neighborhood}</p>
                    </div>
                    <div className="absolute top-2 right-2 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <i className="ri-zoom-in-line text-white"></i>
                    </div>
                  </motion.button>
                ))}
              </div>

            </motion.div>
          </div>
        </div>

        {/* Photo Lightbox */}
        <AnimatePresence>
          {selectedPhoto && currentPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-earth-900/95 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedPhoto(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative max-w-4xl w-full rounded-2xl overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <img 
                  src={currentPhoto.url} 
                  alt={currentPhoto.location}
                  className="w-full max-h-[80vh] object-contain bg-black/50"
                />
                <button 
                  onClick={() => setSelectedPhoto(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                >
                  <i className="ri-close-line text-xl text-earth-800"></i>
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Por que ao ar livre */}
      <Section bgColor="sand">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <SlideIn direction="left">
            <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <i className="ri-lightbulb-line"></i>
              Por que funciona
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-6">
              O cérebro aprende melhor<br />
              <span className="text-olive-600">em contexto real</span>
            </h2>
            <p className="text-earth-600 text-lg mb-6 leading-relaxed">
              Estudos de neurociência mostram que aprendemos e retemos informações muito 
              melhor quando estamos em ambientes reais e emocionalmente engajados. 
              Uma aula em um café ativa memórias sensoriais que uma sala de aula não consegue.
            </p>
            
            <div className="space-y-4">
              {[
                { icon: 'ri-brain-line', title: 'Memória contextual', desc: 'Associe vocabulário a experiências reais' },
                { icon: 'ri-emotion-happy-line', title: 'Engajamento emocional', desc: 'Aprenda com prazer, não com pressão' },
                { icon: 'ri-shield-check-line', title: 'Confiança prática', desc: 'Pratique antes de precisar de verdade' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                  <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-xl text-olive-600`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-earth-800">{item.title}</h4>
                    <p className="text-earth-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </SlideIn>
          
          <SlideIn direction="right">
            <Card className="p-8 bg-gradient-to-br from-olive-600 to-olive-700 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-award-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Certificado de Imersão</h3>
                  <p className="text-olive-200 text-sm">Beyond Class Outdoor Experience</p>
                </div>
              </div>
              
              <p className="text-olive-100 mb-6 leading-relaxed">
                Ao completar nosso programa de imersões ao ar livre, você recebe um 
                certificado que comprova sua experiência prática com o inglês em 
                situações reais.
              </p>
              
              <div className="space-y-3 mb-6">
                {[
                  'Mínimo de 2 imersões completadas',
                  'Avaliação de progresso pelo professor',
                  'Certificado digital e físico',
                  'Válido para horas complementares',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <i className="ri-check-double-line text-olive-300"></i>
                    <span className="text-sm text-olive-100">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="pt-4 border-t border-white/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">150+</p>
                    <p className="text-olive-200 text-sm">Certificados emitidos</p>
                  </div>
                  <i className="ri-medal-line text-5xl text-white/20"></i>
                </div>
              </div>
            </Card>
          </SlideIn>
        </div>
      </Section>

      {/* Locais em Recife - Interativo */}
      <Section bgColor="white" id="galeria">
        <FadeInUp className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-olive-100 text-olive-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-map-pin-2-line"></i>
            Nossos points em Recife
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
            Onde já fizemos imersões
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-lg">
            Conheça os lugares especiais que escolhemos para nossas aulas ao ar livre
          </p>
        </FadeInUp>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Location tabs */}
          <div className="lg:col-span-2 space-y-3">
            {RECIFE_LOCATIONS.map((location, index) => (
              <motion.button
                key={location.title}
                onClick={() => setActiveLocation(index)}
                onMouseEnter={() => setHoveredSpot(index)}
                onMouseLeave={() => setHoveredSpot(null)}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 ${
                  activeLocation === index 
                    ? 'bg-olive-600 text-white shadow-lg' 
                    : 'bg-sand-50 hover:bg-sand-100 border border-sand-200'
                }`}
                whileHover={{ x: activeLocation === index ? 0 : 5 }}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    activeLocation === index ? 'bg-white/20' : 'bg-olive-100'
                  }`}>
                    <i className={`${location.icon} text-xl ${
                      activeLocation === index ? 'text-white' : 'text-olive-600'
                    }`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${
                      activeLocation === index ? 'text-white' : 'text-earth-800'
                    }`}>
                      {location.title}
                    </h3>
                    <p className={`text-sm ${
                      activeLocation === index ? 'text-olive-200' : 'text-earth-500'
                    }`}>
                      {location.neighborhood}
                    </p>
                  </div>
                  <i className={`ri-arrow-right-s-line text-xl ${
                    activeLocation === index ? 'text-white' : 'text-earth-400'
                  }`}></i>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Location detail */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeLocation}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl overflow-hidden shadow-xl border border-sand-200"
              >
                <div className="relative h-64">
                  <img 
                    src={RECIFE_LOCATIONS[activeLocation].image} 
                    alt={RECIFE_LOCATIONS[activeLocation].title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-earth-900/60 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {RECIFE_LOCATIONS[activeLocation].title}
                    </h3>
                    <p className="text-sand-200 flex items-center gap-2">
                      <i className="ri-map-pin-line"></i>
                      {RECIFE_LOCATIONS[activeLocation].neighborhood}
                    </p>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-earth-600 mb-6 leading-relaxed">
                    {RECIFE_LOCATIONS[activeLocation].desc}
                  </p>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-olive-600 mb-3">Temas que praticamos:</p>
                    <div className="flex flex-wrap gap-2">
                      {RECIFE_LOCATIONS[activeLocation].topics.map((topic) => (
                        <span 
                          key={topic}
                          className="bg-olive-50 text-olive-700 px-3 py-1 rounded-full text-sm"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Section>


      {/* O que você pratica */}
      <Section bgColor="sand">
        <FadeInUp className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-earth-800 mb-4">
            O que você pratica
          </h2>
          <p className="text-earth-600 max-w-2xl mx-auto text-lg">
            Situações reais que você vai usar em viagens e no dia a dia
          </p>
        </FadeInUp>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: 'ri-restaurant-line', title: 'Pedidos', desc: 'Café, restaurante, bar' },
            { icon: 'ri-question-answer-line', title: 'Small talk', desc: 'Conversas casuais' },
            { icon: 'ri-map-pin-line', title: 'Direções', desc: 'Perguntar e explicar' },
            { icon: 'ri-shopping-bag-line', title: 'Compras', desc: 'Lojas e mercados' },
            { icon: 'ri-camera-line', title: 'Turismo', desc: 'Pontos turísticos' },
            { icon: 'ri-phone-line', title: 'Telefone', desc: 'Ligações e recados' },
            { icon: 'ri-bank-card-line', title: 'Pagamentos', desc: 'Conta, gorjeta, troco' },
            { icon: 'ri-emotion-line', title: 'Opiniões', desc: 'Expressar preferências' },
          ].map((skill, index) => (
            <motion.div
              key={skill.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.03, y: -3 }}
              className="bg-sand-50 hover:bg-olive-50 p-5 rounded-xl text-center transition-colors cursor-pointer border border-transparent hover:border-olive-200"
            >
              <div className="w-12 h-12 bg-olive-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                <i className={`${skill.icon} text-xl text-olive-600`}></i>
              </div>
              <h4 className="font-semibold text-earth-800 mb-1">{skill.title}</h4>
              <p className="text-earth-500 text-sm">{skill.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>
    </>
  );
}
